import React, { useState, useEffect, useRef } from 'react';
import { HiChevronDown, HiCheck, HiOutlineExclamationCircle, HiOutlineX, HiPencil } from 'react-icons/hi';
import AppLayout from '../../components/layout/AppLayout';
import Button from '../../components/ui/Button';
import FormInput from '../../components/forms/FormInput';
import { useAuth } from '../../contexts/AuthContext';
import {
  getApartments,
  getLatestReading,
  storeReading,
  updateReading,
  getGasReadings,
  exportReadings,
  exportReadingsNbh,
  checkLateReadings,
  getNbhExportHistory,
  getNbhExportHistoryDetail,
  recallNbhReadings,
  getGasSettings,
} from '../../api/gasReadings';

const TOWERS = ['A', 'B'];
const FLOORS = Array.from({ length: 17 }, (_, i) => i + 2);
const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: new Date(2024, i).toLocaleString('default', { month: 'short' }),
}));
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from({ length: CURRENT_YEAR - 2020 + 1 }, (_, i) => 2020 + i).map((y) => ({ value: String(y), label: String(y) }));

function getAvailableMonths(selectedYear) {
  const maxMonth = selectedYear === CURRENT_YEAR ? CURRENT_MONTH : 12;
  return MONTHS.slice(0, maxMonth);
}
const TOWER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'A', label: 'Tower A' },
  { value: 'B', label: 'Tower B' },
];

function GridDropdown({ label, placeholder, value, displayValue, options, onChange, columns = 6, smColumns = 9 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const gridCols = {
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
  };
  const smGridCols = {
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
    6: 'sm:grid-cols-6',
    7: 'sm:grid-cols-7',
    8: 'sm:grid-cols-8',
    9: 'sm:grid-cols-9',
  };

  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-sm font-semibold text-gray-900 mb-2">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
          open
            ? 'border-primary-500 ring-4 ring-primary-500/10 bg-white'
            : value
              ? 'border-primary-200 bg-primary-50 text-primary-800'
              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
        }`}
      >
        <span className={value ? 'text-gray-900 font-semibold' : ''}>{displayValue || placeholder}</span>
        <HiChevronDown className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''} ${value ? 'text-primary-600' : 'text-gray-400'}`} />
      </button>

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg p-3 animate-in fade-in slide-in-from-top-1">
          <div className={`grid ${gridCols[columns] || 'grid-cols-6'} ${smGridCols[smColumns] || 'sm:grid-cols-9'} gap-1.5`}>
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`relative py-2.5 px-1 rounded-lg text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-primary-700 text-white shadow-sm'
                      : 'bg-gray-50 text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                  }`}
                >
                  {opt.label}
                  {isSelected && (
                    <HiCheck className="absolute top-0.5 right-0.5 w-3 h-3" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordReadingTab() {
  const [tower, setTower] = useState('');
  const [floor, setFloor] = useState('');
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [latestReading, setLatestReading] = useState(null);
  const [currentReading, setCurrentReading] = useState('');
  const [readingDate, setReadingDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [isLateReading, setIsLateReading] = useState(false);
  const [settings, setSettings] = useState({ price_per_unit: 77.58 });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getGasSettings().then((res) => {
      if (res.data) setSettings(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tower && floor) {
      setLoading(true);
      getApartments({ tower, floor }).then((res) => {
        setApartments(res.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setApartments([]);
    }
    setSelectedApartment(null);
    setLatestReading(null);
    setCurrentReading('');
    setIsLateReading(false);
  }, [tower, floor]);

  const handleApartmentSelect = async (apt) => {
    setSelectedApartment(apt);
    setCurrentReading('');
    setIsLateReading(false);
    setMessage('');
    try {
      const res = await getLatestReading(apt.id);
      setLatestReading(res.data);
    } catch {
      setLatestReading(null);
    }
  };

  const previousM3 = latestReading ? parseFloat(latestReading.current_reading_m3) : 0;
  const currentM3 = parseFloat(currentReading) || 0;
  const consumptionM3 = currentM3 - previousM3;
  const pricePerUnit = parseFloat(settings.price_per_unit) || 77.58;
  const totalPrice = consumptionM3 * pricePerUnit;

  const handleSave = async () => {
    if (!selectedApartment || !currentReading) return;
    setSaving(true);
    setMessage('');
    try {
      await storeReading({
        apartment_id: selectedApartment.id,
        current_reading_m3: parseFloat(currentReading),
        reading_date: readingDate,
        notes: notes || null,
        is_late_reading: isLateReading,
      });
      setMessage(isLateReading ? 'Late reading saved! It will be included in the next NBH export.' : 'Reading saved successfully!');
      setCurrentReading('');
      setNotes('');
      setIsLateReading(false);
      setSelectedApartment(null);
      setLatestReading(null);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to save reading');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {message && (
        <div className={`px-4 py-2 rounded-lg text-sm ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Tower Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Tower</h3>
        <div className="flex gap-3">
          {TOWERS.map((t) => (
            <button
              key={t}
              onClick={() => { setTower(t); setFloor(''); }}
              className={`flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-lg text-sm font-semibold transition-all ${
                tower === t
                  ? 'bg-primary-700 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tower {t}
            </button>
          ))}
        </div>
      </div>

      {/* Floor & Apartment Selection */}
      {tower && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <GridDropdown
              label="Floor"
              placeholder="Select Floor"
              value={floor}
              displayValue={floor ? `Floor ${floor}` : ''}
              options={FLOORS.map((f) => ({ value: String(f), label: String(f) }))}
              onChange={(val) => setFloor(val)}
              columns={6}
              smColumns={9}
            />
            <div>
              {floor ? (
                loading ? (
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Apartment</label>
                    <div className="flex items-center justify-center py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-sm text-gray-400">
                      Loading...
                    </div>
                  </div>
                ) : (
                  <GridDropdown
                    label="Apartment"
                    placeholder="Select Apartment"
                    value={selectedApartment ? String(selectedApartment.id) : ''}
                    displayValue={selectedApartment ? `${selectedApartment.unit} — ${selectedApartment.owner_name || 'No owner'}` : ''}
                    options={apartments.map((apt) => ({ value: String(apt.id), label: apt.unit }))}
                    onChange={(val) => {
                      const apt = apartments.find((a) => String(a.id) === val);
                      if (apt) handleApartmentSelect(apt);
                    }}
                    columns={4}
                    smColumns={7}
                  />
                )
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Apartment</label>
                  <div className="flex items-center justify-center py-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-sm text-gray-400">
                    Select a floor first
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reading Form */}
      {selectedApartment && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Apartment {selectedApartment.unit} — Tower {tower}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs text-gray-500 mb-1">Owner</p>
              <p className="text-sm font-medium text-gray-900">{selectedApartment.owner_name || 'N/A'}</p>
              {selectedApartment.owner_phone && (
                <p className="text-xs text-gray-500 mt-1">{selectedApartment.owner_phone}</p>
              )}
            </div>
            <div className={`rounded-lg p-3 sm:p-4 ${latestReading?.is_late_reading && !latestReading?.settled_in_month ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <p className="text-xs text-gray-500">Previous Reading</p>
                {latestReading?.is_late_reading && !latestReading?.settled_in_month && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800">LATE</span>
                )}
              </div>
              <p className="text-sm font-medium text-gray-900">{previousM3.toFixed(3)} m³</p>
              {latestReading && (
                <p className="text-xs text-gray-500 mt-1">Date: {latestReading.reading_date}</p>
              )}
              {latestReading?.is_late_reading && !latestReading?.settled_in_month && (
                <p className="text-xs text-amber-600 mt-1">This is a late reading — not yet billed.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <FormInput
              label="Current Reading (m³)"
              type="number"
              step="0.001"
              value={currentReading}
              onChange={(e) => setCurrentReading(e.target.value)}
              required
            />
            <FormInput
              label="Reading Date"
              type="date"
              value={readingDate}
              onChange={(e) => setReadingDate(e.target.value)}
              required
            />
          </div>

          <FormInput
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mb-4"
          />

          {currentReading && consumptionM3 >= 0 && (
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mb-4">
              <h4 className="text-xs font-semibold text-blue-800 mb-2">Calculated Values</h4>
              <div className="grid grid-cols-2 gap-2 sm:gap-4 text-sm">
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600">Consumption (m³)</p>
                  <p className="font-semibold text-blue-900 text-sm sm:text-base">{consumptionM3.toFixed(3)}</p>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-blue-600">Total Price</p>
                  <p className="font-semibold text-blue-900 text-sm sm:text-base">₹{totalPrice.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button onClick={handleSave} loading={saving} disabled={!currentReading || consumptionM3 < 0}>
              Save Reading
            </Button>
            <label className="flex items-center gap-2.5 cursor-pointer select-none group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isLateReading}
                  onChange={e => setIsLateReading(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  isLateReading ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white group-hover:border-amber-400'
                }`}>
                  {isLateReading && <HiCheck className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Late Reading</p>
                <p className="text-xs text-gray-400">Bill generated after this reading</p>
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadingHistoryTab({ isSuperAdmin }) {
  const [tower, setTower] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editReading, setEditReading] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const fetchReadings = async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (tower) params.tower = tower;
      const res = await getGasReadings(params);
      setReadings(res.data || []);
    } catch {
      setReadings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadings();
  }, [tower, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (r) => {
    setEditReading(r);
    setEditForm({
      current_reading_m3: r.current_reading_m3,
      reading_date: r.reading_date,
      notes: r.notes || '',
      is_late_reading: r.is_late_reading || false,
    });
  };

  const handleEditSave = async () => {
    if (!editReading) return;
    setEditSaving(true);
    try {
      await updateReading(editReading.id, {
        current_reading_m3: parseFloat(editForm.current_reading_m3),
        reading_date: editForm.reading_date,
        notes: editForm.notes || null,
        is_late_reading: editForm.is_late_reading,
      });
      setEditReading(null);
      fetchReadings();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update reading');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <>
      {/* Edit Reading Modal */}
      {editReading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditReading(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-800">Edit Reading</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {editReading.apartment?.unit} · Previous: {parseFloat(editReading.previous_reading_m3).toFixed(3)} m³
                </p>
              </div>
              <button onClick={() => setEditReading(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Current Reading (m³)"
                  type="number"
                  step="0.001"
                  value={editForm.current_reading_m3}
                  onChange={(e) => setEditForm({ ...editForm, current_reading_m3: e.target.value })}
                  required
                />
                <FormInput
                  label="Reading Date"
                  type="date"
                  value={editForm.reading_date}
                  onChange={(e) => setEditForm({ ...editForm, reading_date: e.target.value })}
                  required
                />
              </div>
              <FormInput
                label="Notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
              <label className="flex items-center gap-2.5 cursor-pointer select-none group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={editForm.is_late_reading}
                    onChange={(e) => setEditForm({ ...editForm, is_late_reading: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    editForm.is_late_reading ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white group-hover:border-amber-400'
                  }`}>
                    {editForm.is_late_reading && <HiCheck className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-700">Late Reading</span>
              </label>
            </div>
            <div className="flex gap-3 px-6 pb-5">
              <button
                onClick={() => setEditReading(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving || !editForm.current_reading_m3 || !editForm.reading_date}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#043959,#065a84)' }}
              >
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <GridDropdown
            label="Year"
            placeholder="Select Year"
            value={String(year)}
            displayValue={String(year)}
            options={YEARS}
            onChange={(val) => {
              const y = parseInt(val);
              setYear(y);
              if (y === CURRENT_YEAR && month > CURRENT_MONTH) setMonth(CURRENT_MONTH);
            }}
            columns={4}
            smColumns={7}
          />
          <GridDropdown
            label="Month"
            placeholder="Select Month"
            value={String(month)}
            displayValue={MONTHS.find((m) => m.value === String(month))?.label || ''}
            options={getAvailableMonths(year)}
            onChange={(val) => setMonth(parseInt(val))}
            columns={4}
            smColumns={6}
          />
          <GridDropdown
            label="Tower"
            placeholder="All Towers"
            value={tower}
            displayValue={tower ? `Tower ${tower}` : 'All Towers'}
            options={TOWER_OPTIONS}
            onChange={(val) => setTower(val)}
            columns={3}
            smColumns={3}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading...</div>
        ) : readings.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No readings found for this period.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Owner</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Previous (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Current (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Consumption (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Price (₹)</th>
                    {isSuperAdmin && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody>
                  {readings.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.apartment?.unit}</td>
                      <td className="px-4 py-3 text-gray-600">{r.apartment?.owner_name || '—'}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.previous_reading_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.current_reading_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right">{parseFloat(r.consumption_m3).toFixed(3)}</td>
                      <td className="px-4 py-3 text-right font-medium">₹{parseFloat(r.total_price).toFixed(2)}</td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openEdit(r)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Edit reading"
                          >
                            <HiPencil className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden divide-y divide-gray-100">
              {readings.map((r) => (
                <div key={r.id} className="p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{r.apartment?.unit}</p>
                      <p className="text-xs text-gray-500">{r.apartment?.owner_name || '—'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-primary-700">₹{parseFloat(r.total_price).toFixed(2)}</p>
                      {isSuperAdmin && (
                        <button
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit reading"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-400">Prev (m³)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.previous_reading_m3).toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Curr (m³)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.current_reading_m3).toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Cons (m³)</p>
                      <p className="font-medium text-gray-700">{parseFloat(r.consumption_m3).toFixed(3)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}

function LateReadingsModal({ lateReadings, month, year, onInclude, onSkip, onClose }) {
  // lateReadings is now one item per apartment (aggregated by backend)
  const aptIds = lateReadings.map(r => r.apartment_id);
  const [selected, setSelected] = useState(new Set(aptIds));
  const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

  const allChecked = selected.size === aptIds.length;
  const noneChecked = selected.size === 0;
  const someChecked = !allChecked && !noneChecked;

  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(aptIds));
  const toggleOne = (aptId) => setSelected(prev => {
    const next = new Set(prev);
    next.has(aptId) ? next.delete(aptId) : next.add(aptId);
    return next;
  });

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <HiOutlineExclamationCircle className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-800">Late Readings Found</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {lateReadings.length} apt{lateReadings.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-1">
            These apartments have <span className="font-semibold text-amber-700">unsettled late readings</span> not yet billed.
            Select which to include in the {monthName} {year} NBH export.
          </p>
          <p className="text-xs text-gray-400 mb-4">Included readings will be added to the current month's consumption.</p>

          <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2.5 w-10">
                    <button type="button" onClick={toggleAll} className="flex items-center justify-center w-full">
                      <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-all ${
                        allChecked ? 'bg-amber-500 border-amber-500'
                          : someChecked ? 'bg-amber-100 border-amber-400'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {allChecked && <HiCheck className="w-3 h-3 text-white" />}
                        {someChecked && <span className="w-2 h-0.5 bg-amber-600 block" />}
                      </div>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-600">Unit</th>
                  <th className="text-left px-3 py-2.5 font-medium text-gray-600">Period</th>
                  <th className="text-center px-3 py-2.5 font-medium text-gray-600">Readings</th>
                  <th className="text-right px-3 py-2.5 font-medium text-gray-600">Total Cons (m³)</th>
                </tr>
              </thead>
              <tbody>
                {lateReadings.map((r) => {
                  const isChecked = selected.has(r.apartment_id);
                  const sameDay = r.earliest_date === r.latest_date;
                  return (
                    <tr
                      key={r.apartment_id}
                      onClick={() => toggleOne(r.apartment_id)}
                      className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors ${isChecked ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center mx-auto transition-all ${
                          isChecked ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'
                        }`}>
                          {isChecked && <HiCheck className="w-3 h-3 text-white" />}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-gray-800">T{r.tower} — {r.unit}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                        {sameDay ? fmtDate(r.earliest_date) : `${fmtDate(r.earliest_date)} – ${fmtDate(r.latest_date)}`}
                      </td>
                      <td className="px-3 py-2.5 text-center text-gray-500">{r.reading_count}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${isChecked ? 'text-amber-700' : 'text-gray-400'}`}>
                        {r.total_consumption_m3.toFixed(3)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-gray-400">{selected.size} of {aptIds.length} apartments selected</span>
            <button type="button" onClick={toggleAll}
              className="text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors">
              {allChecked ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onInclude([...selected])}
              disabled={noneChecked}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg,#15803d,#166534)' }}
            >
              Include Selected ({selected.size})
            </button>
            <button
              onClick={onSkip}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 transition-all"
            >
              Skip All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportTab() {
  const [tower, setTower] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [exportingNbh, setExportingNbh] = useState(false);
  const [message, setMessage] = useState('');
  const [lateModal, setLateModal] = useState(null); // { lateReadings: [] }
  const [alreadyBilledModal, setAlreadyBilledModal] = useState(null); // { period, hasLate }

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const doNbhDownload = async (selectedIds = []) => {
    const params = { month, year, ...(tower ? { tower } : {}) };
    if (selectedIds.length > 0) params.include_late_ids = selectedIds.join(',');
    const response = await exportReadingsNbh(params);
    const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'short' });
    downloadBlob(response.data, `NBH_Gas_${monthName}_${year}${tower ? `_Tower${tower}` : ''}.xlsx`);
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage('');
    try {
      const params = { month, year };
      if (tower) params.tower = tower;
      const response = await exportReadings(params);
      downloadBlob(response.data, `gas_readings_${year}_${String(month).padStart(2, '0')}${tower ? `_tower_${tower}` : ''}.xlsx`);
      setMessage('Export downloaded successfully!');
    } catch {
      setMessage('Failed to export readings.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportNbhClick = async () => {
    setExportingNbh(true);
    setMessage('');
    try {
      const params = { month, year, ...(tower ? { tower } : {}) };
      const data = await checkLateReadings(params);

      // No new readings to bill and no pending late readings → warn before allowing re-export
      if (!data.has_unbilled_readings && !data.has_late_readings) {
        const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });
        setAlreadyBilledModal({ period: `${monthName} ${year}`, tower });
        setExportingNbh(false);
        return;
      }

      if (data.has_late_readings) {
        setLateModal({ lateReadings: data.late_readings });
        setExportingNbh(false);
        return;
      }
      await doNbhDownload([]);
      setMessage('NBH export downloaded successfully!');
    } catch {
      setMessage('Failed to export for NBH.');
    } finally {
      setExportingNbh(false);
    }
  };

  const handleForceNbhDownload = async () => {
    setAlreadyBilledModal(null);
    setExportingNbh(true);
    try {
      await doNbhDownload([]);
      setMessage('NBH export re-downloaded.');
    } catch {
      setMessage('Failed to export for NBH.');
    } finally {
      setExportingNbh(false);
    }
  };

  const handleLateInclude = async (selectedIds) => {
    setLateModal(null);
    setExportingNbh(true);
    try {
      await doNbhDownload(selectedIds);
      setMessage(`NBH export downloaded with ${selectedIds.length} late reading(s) included. Those readings are now settled.`);
    } catch {
      setMessage('Failed to export for NBH.');
    } finally {
      setExportingNbh(false);
    }
  };

  const handleLateSkip = async () => {
    setLateModal(null);
    setExportingNbh(true);
    try {
      await doNbhDownload([]);
      setMessage('NBH export downloaded (late readings not included).');
    } catch {
      setMessage('Failed to export for NBH.');
    } finally {
      setExportingNbh(false);
    }
  };

  return (
    <>
      {alreadyBilledModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setAlreadyBilledModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <HiCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">All Readings Already Billed</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {alreadyBilledModal.period}{alreadyBilledModal.tower ? ` · Tower ${alreadyBilledModal.tower}` : ''}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              All gas readings for this period have already been exported to NBH. No new readings are pending for billing.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setAlreadyBilledModal(null)}
                className="flex-1 px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                OK
              </button>
              <button
                onClick={handleForceNbhDownload}
                className="flex-1 px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium"
              >
                Re-download Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {lateModal && (
        <LateReadingsModal
          lateReadings={lateModal.lateReadings}
          month={month}
          year={year}
          onInclude={handleLateInclude}
          onSkip={handleLateSkip}
          onClose={() => setLateModal(null)}
        />
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Export Gas Readings</h3>
        {message && (
          <div className={`px-4 py-2 rounded-lg text-sm mb-4 ${message.includes('success') || message.includes('settled') || message.includes('included') ? 'bg-green-50 text-green-700' : message.includes('not included') || message.includes('without') ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
            {message}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <GridDropdown
            label="Year"
            placeholder="Select Year"
            value={String(year)}
            displayValue={String(year)}
            options={YEARS}
            onChange={(val) => {
              const y = parseInt(val);
              setYear(y);
              if (y === CURRENT_YEAR && month > CURRENT_MONTH) setMonth(CURRENT_MONTH);
            }}
            columns={4}
            smColumns={7}
          />
          <GridDropdown
            label="Month"
            placeholder="Select Month"
            value={String(month)}
            displayValue={MONTHS.find((m) => m.value === String(month))?.label || ''}
            options={getAvailableMonths(year)}
            onChange={(val) => setMonth(parseInt(val))}
            columns={4}
            smColumns={6}
          />
          <GridDropdown
            label="Tower"
            placeholder="All Towers"
            value={tower}
            displayValue={tower ? `Tower ${tower}` : 'All Towers'}
            options={TOWER_OPTIONS}
            onChange={(val) => setTower(val)}
            columns={3}
            smColumns={3}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleExport} loading={exporting}>Export to Excel</Button>
          {/* Temporarily hidden — will be re-enabled later */}
          {false && (
            <button
              onClick={handleExportNbhClick}
              disabled={exportingNbh}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#15803d,#166534)' }}
            >
              {exportingNbh ? 'Checking…' : 'Export for NBH'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const TABS = [
  { key: 'record', label: 'Record' },
  { key: 'history', label: 'History' },
  { key: 'export', label: 'Export' },
  { key: 'nbh-history', label: 'NBH History' },
];

function NbhDetailModal({ log, onClose, onRecalled }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [recalling, setRecalling] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    getNbhExportHistoryDetail(log.id)
      .then((data) => {
        setDetail(data);
        const recallable = new Set(
          data.readings.filter((r) => !r.is_recalled).map((r) => r.apartment_id)
        );
        setSelected(recallable);
      })
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [log.id]);

  const recallableReadings = detail?.readings.filter((r) => !r.is_recalled) ?? [];
  const allSelected = selected.size === recallableReadings.length && recallableReadings.length > 0;
  const noneSelected = selected.size === 0;

  const toggleOne = (aptId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(aptId)) { next.delete(aptId); } else { next.add(aptId); }
      return next;
    });
  };

  const toggleAll = () => {
    const aptIds = (detail?.readings ?? []).filter((r) => !r.is_recalled).map((r) => r.apartment_id);
    setSelected((prev) => {
      const allAreSelected = aptIds.length > 0 && aptIds.every((id) => prev.has(id));
      return allAreSelected ? new Set() : new Set(aptIds);
    });
  };

  const handleRecall = async () => {
    if (noneSelected) { return; }
    setRecalling(true);
    try {
      const aptIds = [...selected];
      await recallNbhReadings(log.id, aptIds);
      onRecalled();
      onClose();
    } catch {
      setRecalling(false);
    }
  };

  const fmt = (n) => n != null ? n.toFixed(3) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 sm:pt-12"
      style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3"
          style={{ background: 'linear-gradient(135deg,#fff7ed,#ffedd5)' }}>
          <div>
            <h3 className="font-semibold text-gray-800">
              NBH Export — {log.billed_period}
              {log.tower ? ` · Tower ${log.tower}` : ''}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {log.apartments_billed} apartment{log.apartments_billed !== 1 ? 's' : ''} billed
              {log.recalled_count > 0 && (
                <span className="ml-2 text-amber-600 font-medium">{log.recalled_count} recalled</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors mt-0.5">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loadingDetail ? (
            <div className="p-10 text-center text-gray-400 text-sm">Loading readings…</div>
          ) : !detail || detail.readings.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">No readings linked to this export.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 sticky top-0">
                    <th className="px-4 py-3 w-8">
                      {recallableReadings.length > 0 && (
                        <div
                          onClick={toggleAll}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                            allSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300 hover:border-orange-400'
                          }`}
                        >
                          {allSelected && <HiCheck className="w-2.5 h-2.5 text-white" />}
                        </div>
                      )}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Unit</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Tower</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Date</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Prev (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Curr (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Cons (m³)</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Amount</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.readings.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => !r.is_recalled && toggleOne(r.apartment_id)}
                      className={`border-b border-gray-50 transition-colors ${
                        r.is_recalled
                          ? 'bg-gray-50 opacity-60'
                          : selected.has(r.apartment_id)
                          ? 'bg-orange-50 cursor-pointer hover:bg-orange-100'
                          : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        {!r.is_recalled && (
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                              selected.has(r.apartment_id)
                                ? 'bg-orange-500 border-orange-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {selected.has(r.apartment_id) && <HiCheck className="w-2.5 h-2.5 text-white" />}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{r.unit}</div>
                        {r.has_late_reading && (
                          <span className="text-xs font-semibold text-amber-600">+ late</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                        {r.tower ? `Tower ${r.tower}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden sm:table-cell whitespace-nowrap">
                        {new Date(r.reading_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmt(r.billed_previous_m3)}</td>
                      <td className="px-4 py-3 text-right text-gray-700 font-medium">{fmt(r.current_reading_m3)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmt(r.billed_consumption_m3)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-800 hidden md:table-cell">
                        ₹{r.billed_amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.is_recalled ? (
                          <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                            Recalled
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Billed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loadingDetail && recallableReadings.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-500">
              {selected.size > 0 ? (
                <>
                  <span className="font-semibold text-gray-700">{selected.size}</span> of{' '}
                  {recallableReadings.length} selected —{' '}
                  <button onClick={toggleAll} className="text-orange-600 hover:underline text-xs font-medium">
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </>
              ) : (
                <span>
                  No apartments selected —{' '}
                  <button onClick={toggleAll} className="text-orange-600 hover:underline text-xs font-medium">
                    Select All
                  </button>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              {!confirmAll ? (
                <button
                  disabled={noneSelected || recalling}
                  onClick={() => setConfirmAll(true)}
                  className={`flex-1 sm:flex-none px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                    noneSelected
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  Recall Selected ({selected.size})
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-orange-700 font-medium">Confirm recall?</span>
                  <button
                    onClick={() => setConfirmAll(false)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRecall}
                    disabled={recalling}
                    className="px-3 py-1.5 text-xs rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-60"
                  >
                    {recalling ? 'Recalling…' : 'Yes, Recall'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {!loadingDetail && recallableReadings.length === 0 && detail?.readings.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function NbhExportHistoryTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLog, setDetailLog] = useState(null);

  const loadLogs = () => {
    setLoading(true);
    getNbhExportHistory()
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLogs(); }, []);

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  return (
    <>
      {detailLog && (
        <NbhDetailModal
          log={detailLog}
          onClose={() => setDetailLog(null)}
          onRecalled={() => { setDetailLog(null); loadLogs(); }}
        />
      )}
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)' }}>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">NBH Export History</h3>
              <p className="text-xs text-gray-500 mt-0.5">Every time "Export for NBH" was generated</p>
            </div>
            {!loading && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                {logs.length} export{logs.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-3xl mb-2">📋</p>
              <p className="text-gray-500 text-sm font-medium">No NBH exports yet</p>
              <p className="text-gray-400 text-xs mt-1">Exports will appear here after the first bill is generated.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Generated On</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Billed Period</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Tower</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Apts Billed</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Late Readings</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Recalled</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Exported By</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(log.exported_at)}</td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-800">{log.billed_period}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {log.tower ? `Tower ${log.tower}` : <span className="text-gray-400">All</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-800">{log.apartments_billed}</td>
                        <td className="px-4 py-3 text-right">
                          {log.late_readings_included > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              +{log.late_readings_included} late
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {log.recalled_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                              {log.recalled_count} recalled
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{log.exported_by || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDetailLog(log)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-100">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-800">{log.billed_period}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(log.exported_at)}</p>
                      </div>
                      <button
                        onClick={() => setDetailLog(log)}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex-shrink-0"
                      >
                        View
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-gray-500">{log.apartments_billed} apts</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-500">{log.tower ? `Tower ${log.tower}` : 'All Towers'}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-500">{log.exported_by || 'Unknown'}</span>
                      {log.late_readings_included > 0 && (
                        <span className="font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          +{log.late_readings_included} late
                        </span>
                      )}
                      {log.recalled_count > 0 && (
                        <span className="font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          {log.recalled_count} recalled
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function GasReadingsPage() {
  const [activeTab, setActiveTab] = useState('record');
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  return (
    <AppLayout title="Gas Readings" breadcrumbs="Gas Readings">
      <div className="mb-4 sm:mb-6">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'record' && <RecordReadingTab />}
      {activeTab === 'history' && <ReadingHistoryTab isSuperAdmin={isSuperAdmin} />}
      {activeTab === 'export' && <ExportTab />}
      {activeTab === 'nbh-history' && <NbhExportHistoryTab />}
    </AppLayout>
  );
}
